import React from 'react';
import { motion } from 'framer-motion';
import { Section } from '../ui/Section';
import { Container } from '../ui/Container';
import { TechnicalGrid } from '../ui/TechnicalGrid';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import { StatusIndicator } from '../ui/StatusIndicator';
import { ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
export function CaseStudies() {
  const studies = [{
    client: 'ECG Engineering',
    metric: 75,
    label: 'Faster Transmittals',
    detail: 'Saved 50+ hours/month and reduced admin time by 70%.',
    quote: 'LAI understood our workflows immediately. No generic IT consultant speak—just practical solutions that work.',
    person: 'Project Director',
    color: 'tech-green',
    chartData: [{
      name: 'Before',
      value: 100
    }, {
      name: 'After',
      value: 25
    }]
  }, {
    client: 'Grid Construction',
    metric: 70,
    label: 'Reduction in Admin',
    detail: 'Automated site diaries and safety reporting workflows.',
    quote: "Our team actually uses the system. That's never happened with previous consultants.",
    person: 'Operations Manager',
    color: 'tech-blue',
    chartData: [{
      name: 'Before',
      value: 100
    }, {
      name: 'After',
      value: 30
    }]
  }, {
    client: 'McGill Engineering',
    metric: 40,
    label: 'Efficiency Gain',
    detail: 'Streamlined procurement and variation approvals.',
    quote: 'The ROI was clear within the first month. We should have done this years ago.',
    person: 'Engineering Director',
    color: 'tech-green',
    chartData: [{
      name: 'Before',
      value: 100
    }, {
      name: 'After',
      value: 140
    }]
  }];
  return <Section className="bg-dashboard-surface border-y border-dashboard-border" data-id="element-25">
      <TechnicalGrid data-id="element-26">
        <Container className="relative z-10" data-id="element-27">
          {/* Header */}
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-28">
            <div className="flex items-center gap-3 mb-6" data-id="element-29">
              <StatusIndicator status="online" label="Verified Results" data-id="element-30" />
              <span className="text-xs font-mono text-dashboard-muted uppercase" data-id="element-31">
                // PERFORMANCE_METRICS
              </span>
            </div>
            <h2 className="text-4xl font-black tracking-tight text-dashboard-text sm:text-5xl mb-6" data-id="element-32">
              Real Results from{' '}
              <span className="text-gradient-tech" data-id="element-33">Engineers Like You</span>
            </h2>
            <p className="text-lg text-dashboard-text leading-relaxed max-w-2xl" data-id="element-34">
              Not vague promises. Actual time saved, actual admin reduced,
              actual ROI delivered.
            </p>
          </motion.div>

          {/* Case study cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-35">
            {studies.map((study, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="metric-card group hover:border-tech-blue/50 transition-all" data-id="element-36">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-dashboard-border" data-id="element-37">
                  <div data-id="element-38">
                    <div className="text-xs font-mono text-dashboard-muted mb-1" data-id="element-39">
                      CLIENT_ID_{index + 1}
                    </div>
                    <div className="text-sm font-bold text-dashboard-text" data-id="element-40">
                      {study.client}
                    </div>
                  </div>
                  <StatusIndicator status="online" data-id="element-41" />
                </div>

                {/* Metric */}
                <div className="mb-6" data-id="element-42">
                  <AnimatedCounter value={study.metric} suffix="%" className={`text-5xl font-black text-${study.color}`} data-id="element-43" />
                  <div className={`text-lg font-bold text-${study.color} mt-2`} data-id="element-44">
                    {study.label}
                  </div>
                  <div className="text-sm text-dashboard-muted mt-2 font-mono" data-id="element-45">
                    {study.detail}
                  </div>
                </div>

                {/* Chart */}
                <div className="h-32 mb-6" data-id="element-46">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-47">
                    <BarChart data={study.chartData} data-id="element-48">
                      <XAxis dataKey="name" tick={{
                    fill: '#71717a',
                    fontSize: 10
                  }} axisLine={{
                    stroke: '#2a2d3a'
                  }} data-id="element-49" />
                      <YAxis tick={{
                    fill: '#71717a',
                    fontSize: 10
                  }} axisLine={{
                    stroke: '#2a2d3a'
                  }} data-id="element-50" />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-51">
                        {study.chartData.map((entry, idx) => <Cell key={`cell-${idx}`} fill={idx === 0 ? '#ff4757' : study.color === 'tech-green' ? '#00ff88' : '#00d4ff'} data-id="element-52" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Quote */}
                <div className="pt-6 border-t border-dashboard-border" data-id="element-53">
                  <div className="text-xs font-mono text-tech-blue mb-2" data-id="element-54">
                    // TESTIMONIAL
                  </div>
                  <p className="text-sm text-dashboard-text italic mb-3 leading-relaxed" data-id="element-55">
                    "{study.quote}"
                  </p>
                  <p className="text-xs font-mono text-dashboard-muted" data-id="element-56">
                    — {study.person}
                  </p>
                </div>

                {/* CTA */}
                <button className="flex items-center text-sm font-mono text-tech-blue hover:text-tech-green transition-colors group mt-6" data-id="element-57">
                  READ_CASE_STUDY
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-58" />
                </button>
              </motion.div>)}
          </div>
        </Container>
      </TechnicalGrid>
    </Section>;
}